import React, { useState, useEffect } from 'react';
import { X, MapPin, Loader2, Search, PackageSearch } from 'lucide-react';
import { pickingService } from '../../../../../services/pickingService';
import { AggregatedPickingItemDto } from '../../../../../types/picking';
import { useWarehouse } from '../../../../../contexts/WarehouseContext';

interface AggregatedItemsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const AggregatedItemsModal: React.FC<AggregatedItemsModalProps> = ({
  isOpen,
  onClose
}) => {
  const [items, setItems] = useState<AggregatedPickingItemDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { currentWarehouse } = useWarehouse();

  useEffect(() => {
    if (isOpen) {
      loadItems();
    }
  }, [isOpen]);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pickingService.getAggregatedItems(currentWarehouse || undefined);
      setItems(data);
    } catch (err: any) {
      setError(err.message || 'Kunne ikke hente vareliste');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const filteredItems = items.filter(item =>
    (item.itemNo?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.description?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.stdLocationName?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (item.itemBarCode?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const totalRemainingQty = filteredItems.reduce((sum, item) => sum + item.remainingQty, 0);

  // Group items by location for visual grouping
  const locationGroups = filteredItems.reduce<Record<string, AggregatedPickingItemDto[]>>((acc, item) => {
    const loc = item.stdLocationName || 'Ingen lokasjon';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(item);
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h2 className="text-lg font-bold flex items-center gap-2">
              <PackageSearch size={22} className="text-amber-600" />
              Samleplukk - Vareliste
            </h2>
            <p className="text-sm text-gray-500">
              Alle varer fra godkjente plukklister (status 10)
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Filtrer etter varenr, beskrivelse, strekkode eller lokasjon..."
              className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin mr-2 text-amber-600" size={24} />
              <span className="text-gray-600">Laster vareliste...</span>
            </div>
          )}

          {error && (
            <div className="text-red-600 text-center py-8 bg-red-50 rounded-lg">
              {error}
            </div>
          )}

          {!loading && !error && filteredItems.length === 0 && (
            <div className="text-gray-500 text-center py-12">
              <PackageSearch size={48} className="mx-auto mb-3 text-gray-300" />
              <p className="font-medium">Ingen varer å plukke</p>
              <p className="text-sm mt-1">Det finnes ingen godkjente plukklister med gjenstående varer.</p>
            </div>
          )}

          {!loading && !error && filteredItems.length > 0 && (
            <div className="space-y-4">
              {Object.entries(locationGroups).map(([location, locationItems]) => (
                <div key={location}>
                  {/* Location header */}
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <MapPin size={14} className="text-blue-500" />
                    <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
                      {location}
                    </span>
                    <div className="flex-1 h-px bg-blue-100" />
                  </div>

                  {/* Items in this location */}
                  <div className="space-y-1.5">
                    {locationItems.map((item) => (
                      <div
                        key={item.itemNo}
                        className="bg-gray-50 hover:bg-gray-100 rounded-lg p-3 flex items-center justify-between transition-colors"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-semibold">{item.itemNo}</span>
                            {item.itemBarCode && (
                              <span className="text-xs text-gray-400 bg-gray-200 px-1.5 py-0.5 rounded">
                                {item.itemBarCode}
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-600 truncate mt-0.5">
                            {item.description || 'Ingen beskrivelse'}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">
                            {item.numberOfPickingLists} plukkliste{item.numberOfPickingLists !== 1 ? 'r' : ''}
                          </div>
                        </div>
                        <div className="text-right ml-4 flex-shrink-0">
                          <div className="text-xl font-bold text-blue-600">
                            {item.remainingQty}
                          </div>
                          <div className="text-xs text-gray-500">{item.unit || 'stk'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer with summary */}
        {!loading && filteredItems.length > 0 && (
          <div className="border-t p-4 bg-gray-50 rounded-b-xl">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">
                {filteredItems.length} ulike vare{filteredItems.length !== 1 ? 'r' : ''}
                {Object.keys(locationGroups).length > 0 && (
                  <span className="ml-2 text-gray-400">
                    ({Object.keys(locationGroups).length} lokasjon{Object.keys(locationGroups).length !== 1 ? 'er' : ''})
                  </span>
                )}
              </span>
              <span className="font-semibold text-blue-700">
                Totalt: {totalRemainingQty} enheter
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
